import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from "app/pages/DiscountEditor/actionTypes";
import { Alert } from 'antd';
import { CheckCircleOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import "./SaveAndBackButtons.css";
import { saveDiscount } from 'app/pages/DiscountEditor/save';
import { DiscountSaveStatus, getDiscountSaveStatus, hasSaveError, getId, hasBeenSaved, getAllErrors, shouldShowSuccessMessage } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveErrorValue } from 'app/pages/DiscountEditor/modules/Editor/state';
import GenericSaveAndBackButton, {SaveState}  from 'app/shared/ui/GenericSaveAndBackButton'

interface SaveAndBackButtonProps {
    navigateToRoot?: () => void
}

interface StateProps { 
    discountId: string
    saveState: SaveState
    hadSaveError: SaveErrorValue
    allErrors: { [category: string]: string[] }
    showSuccess: boolean
}

interface DispatchProps {
    onSaveClicked: () => void,
    onBackClicked?: (evt: React.SyntheticEvent<HTMLAnchorElement>) => void
}

type InternalProps = StateProps & DispatchProps;

function SuccessPanel() {
    return (
        <div className="save-error-container" style={{ marginBottom: 16 }}>
            <Alert
                title="Saved successfully!"
                description="The discount has been saved successfully."
                type="success"
                icon={<CheckCircleOutlined />}
                showIcon
                closable
            />
        </div>
    );
}

function ErrorPanel(errorValue: SaveErrorValue, allErrors: { [category: string]: string[] }) {
    switch(errorValue) {
        case SaveErrorValue.Invalid:
            const errorMessages = Object.values(allErrors).flat();
            return (
                <div className="save-error-container" style={{ marginBottom: 16 }}>
                    <Alert
                        title="There are some problems with the discount"
                        description={
                            errorMessages.length > 0 ? (
                                <ul style={{ marginTop: '8px', paddingLeft: '20px', marginBottom: 0 }}>
                                    {errorMessages.map((msg, idx) => (
                                        <li key={idx}>{msg}</li>
                                    ))}
                                </ul>
                            ) : (
                                "Please fix the highlighted issues and try again"
                            )
                        }
                        type="warning"
                        icon={<InfoCircleOutlined />}
                        showIcon
                        closable
                    />
                </div>
            );

        case SaveErrorValue.Error:
            const allErrorMessages = Object.values(allErrors).flat();
            return (
                <div className="save-error-container" style={{ marginBottom: 16 }}>
                    <Alert
                        title="Failed to save discount"
                        description={
                            allErrorMessages.length > 0 ? (
                                <ul style={{ marginTop: '8px', paddingLeft: '20px', marginBottom: 0 }}>
                                    {allErrorMessages.map((msg, idx) => (
                                        <li key={idx}>{msg}</li>
                                    ))}
                                </ul>
                            ) : (
                                "If the problem persists then please contact support"
                            )
                        }
                        type="error"
                        icon={<WarningOutlined />}
                        showIcon
                        closable
                    />
                </div>
            );

        case SaveErrorValue.NoError:
            return null;
    }
}

const SaveAndBackButtons = (props: InternalProps) => (
    <div>
        <GenericSaveAndBackButton 
            backUrl={"/#" + props.discountId}
            onSaveClicked={props.onSaveClicked}
            backLinkText="Back to discounts"
            saveState={props.saveState}
        />
        {props.showSuccess && SuccessPanel()}
        {ErrorPanel(props.hadSaveError, props.allErrors)}
    </div>   
)

function mapStateToProps(state: any, ownProps: SaveAndBackButtonProps) : StateProps {
    const saveStatus = getDiscountSaveStatus(state); 
    const discountId = getId(state); 

    let saveState = SaveState.Standard;
    switch (saveStatus) {
        case DiscountSaveStatus.NoChanges:
            if (hasBeenSaved(state))
                saveState = SaveState.Saved;
            else
                saveState = SaveState.NoChanges;
            break;
        case DiscountSaveStatus.SavingChanges:
            saveState = SaveState.Saving;
            break;
        case DiscountSaveStatus.ChangesToSave:
        default:
            saveState = SaveState.Standard;
            break;
    }
    
    return {
        discountId: discountId || "",
        saveState: saveState,
        hadSaveError: hasSaveError(state),
        allErrors: getAllErrors(state),
        showSuccess: shouldShowSuccessMessage(state)
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<any, any, Action>, ownProps: SaveAndBackButtonProps) : DispatchProps {
    return {
        onSaveClicked: () => {
            const promise = dispatch(saveDiscount()) as Promise<boolean>;
            if (promise?.then && ownProps.navigateToRoot) {
                promise.then(success => {
                    if (success) ownProps.navigateToRoot!();
                });
            }
        }/*,

        onBackClicked: (evt: React.SyntheticEvent<HTMLAnchorElement>) => {
            evt.preventDefault();
        }*/
    };
}

const ConnectedSaveAndBackButtons = connect(mapStateToProps, mapDispatchToProps)(SaveAndBackButtons);

export default function SaveAndBackButtonsWithNavigate() {
    const navigate = useNavigate();
    return <ConnectedSaveAndBackButtons navigateToRoot={() => navigate('/')} />;
}
